/*
 * Editor de aparencia do portal, com previa em formato de celular.
 *
 * Vive no painel de cada equipamento, ao lado do botao que envia para o
 * roteador. O que se edita aqui vale so para este MikroTik: nao ha heranca, e
 * campo em branco cai no padrao de fabrica.
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ApiRequestError, apiRequest } from '../lib/api';
import { enviarImagem } from '../lib/upload';
import type { HotspotConfigDto, PortalThemeDto, PortalThemeEffective } from '../lib/types';
import { Alert, Button, Card, Field, Spinner } from './ui';

const CORES = [
  { chave: 'backgroundColor', rotulo: 'Fundo da tela' },
  { chave: 'cardColor', rotulo: 'Fundo do cartão' },
  { chave: 'textColor', rotulo: 'Texto principal' },
  { chave: 'mutedColor', rotulo: 'Texto secundário' },
  { chave: 'buttonColor', rotulo: 'Botão' },
  { chave: 'buttonTextColor', rotulo: 'Texto do botão' },
] as const;

const TEXTOS = [
  { chave: 'title', rotulo: 'Título', dica: 'Ex.: Wi-Fi gratuito' },
  { chave: 'subtitle', rotulo: 'Subtítulo', dica: 'Frase abaixo do título' },
  { chave: 'buttonLabel', rotulo: 'Texto do botão', dica: 'Ex.: Conecte-se agora' },
  { chave: 'footerText', rotulo: 'Rodapé', dica: 'Aviso pequeno abaixo do botão' },
  {
    chave: 'successTitle',
    rotulo: 'Título ao conectar',
    dica: 'Use {nome} para o nome do cliente. Ex.: Olá {nome}, tudo certo!',
  },
  {
    chave: 'successMessage',
    rotulo: 'Mensagem ao conectar',
    dica: 'Também aceita {nome}.',
  },
] as const;

const PADRAO: PortalThemeEffective = {
  logoUrl: null,
  backgroundColor: '#0f172a',
  cardColor: '#1e293b',
  textColor: '#e2e8f0',
  mutedColor: '#94a3b8',
  buttonColor: '#2563eb',
  buttonTextColor: '#ffffff',
  title: 'Wi-Fi gratuito',
  subtitle: '',
  buttonLabel: 'Conecte-se agora',
  footerText: '',
  successTitle: 'Olá {nome}, tudo certo!',
  successMessage: '',
};

/**
 * Qual tela do portal a previa esta mostrando.
 * No modo cadastro sao duas, com layouts bem diferentes: quem chega pela
 * primeira vez ve o formulario; quem volta dentro do prazo so confirma.
 */
export type TelaPrevia = 'open' | 'register' | 'retorno' | 'cpf';

/** Campo do formulario, so para dar forma — a previa nao e interativa. */
function CampoFalso({
  tema,
  rotulo,
  exemplo,
}: {
  tema: PortalThemeEffective;
  rotulo: string;
  exemplo: string;
}) {
  return (
    <div className="mb-2 text-left">
      <p className="mb-1 pl-0.5 text-[9px] font-semibold" style={{ color: tema.textColor }}>
        {rotulo}
      </p>
      {/* Raios proporcionais ao portal: 16px num cartao de 380 vira ~11 aqui. */}
      <div
        className="rounded-[11px] px-2.5 py-2 text-[9px]"
        style={{
          border: `1px solid ${tema.mutedColor}3d`,
          background: `${tema.backgroundColor}6b`,
          color: tema.mutedColor,
        }}
      >
        {exemplo}
      </div>
    </div>
  );
}

function MarcacaoFalsa({ tema, texto }: { tema: PortalThemeEffective; texto: string }) {
  return (
    <div
      className="mb-1.5 flex items-start gap-2 rounded-[10px] px-2 py-1.5 text-left"
      style={{ background: `${tema.backgroundColor}47` }}
    >
      <span
        className="mt-px size-3 shrink-0 rounded-[4px]"
        style={{ border: `1.5px solid ${tema.mutedColor}73` }}
      />
      <span className="text-[8px] leading-snug" style={{ color: tema.mutedColor }}>
        {texto}
      </span>
    </div>
  );
}

/** Replica do portal, em proporcao de celular. */
export function PreviaPortal({
  tema,
  tela = 'open',
}: {
  tema: PortalThemeEffective;
  tela?: TelaPrevia;
}) {
  const marca = tema.logoUrl ? (
    <img src={tema.logoUrl} alt="" className="mx-auto mb-3 max-h-14 max-w-[140px] object-contain" />
  ) : (
    /* Sem logo, o emoji mora num bloco arredondado — igual ao portal. */
    <div
      className="mx-auto mb-3 grid size-12 place-items-center rounded-[18px] text-2xl"
      style={{ background: `${tema.buttonColor}26` }}
    >
      {tela === 'retorno' ? '👋' : '📶'}
    </div>
  );

  const botao = (rotulo: string) => (
    <div
      className="mt-3 rounded-full px-3 py-2.5 text-[13px] font-bold"
      style={{
        background: tema.buttonColor,
        color: tema.buttonTextColor,
        boxShadow: `0 8px 18px -8px ${tema.buttonColor}bf`,
      }}
    >
      {rotulo}
    </div>
  );

  const rodape = tema.footerText ? (
    <p className="mt-3 text-[9px] leading-snug" style={{ color: tema.mutedColor }}>
      {tema.footerText}
    </p>
  ) : null;

  return (
    <div
      className="mx-auto w-full max-w-[260px] overflow-hidden rounded-[28px] border-[10px] border-slate-800 shadow-xl"
      style={{ aspectRatio: '9 / 19.5' }}
    >
      <div
        className="flex h-full w-full items-center justify-center p-4"
        style={{
          /* Mesmo brilho radial do portal, na proporcao da previa. */
          background: `radial-gradient(125% 75% at 50% -15%, ${tema.buttonColor}42, transparent 62%), ${tema.backgroundColor}`,
        }}
      >
        <div
          className="w-full rounded-[20px] p-4 text-center"
          style={{
            background: tema.cardColor,
            border: `1px solid ${tema.mutedColor}3d`,
            boxShadow: '0 18px 40px -18px rgb(0 0 0 / 55%)',
          }}
        >
          {marca}

          {tela === 'retorno' ? (
            <>
              {/* Nome de exemplo: o portal cumprimenta pelo primeiro nome. */}
              <p
                className="text-[15px] leading-tight font-semibold"
                style={{ color: tema.textColor }}
              >
                Olá, Ana!
              </p>
              <p className="mt-1.5 text-[10px] leading-snug" style={{ color: tema.mutedColor }}>
                Confirme para voltar a navegar nesta rede.
              </p>
              {botao('Conectar')}
              <p
                className="mt-2 text-[9px] font-semibold underline"
                style={{ color: tema.buttonColor }}
              >
                Não sou eu
              </p>
            </>
          ) : (
            <>
              <p
                className="text-[15px] leading-tight font-semibold"
                style={{ color: tema.textColor }}
              >
                {tema.title}
              </p>
              <p className="mt-1.5 mb-3 text-[10px] leading-snug" style={{ color: tema.mutedColor }}>
                {tema.subtitle ||
                  (tela === 'register'
                    ? 'Preencha seus dados para conectar.'
                    : tela === 'cpf'
                      ? 'Informe seu CPF para conectar.'
                      : '')}
              </p>

              {tela === 'cpf' ? (
                <>
                  <CampoFalso tema={tema} rotulo="Seu CPF" exemplo="000.000.000-00" />
                  <MarcacaoFalsa
                    tema={tema}
                    texto="Li e aceito os Termos de Uso e a Política de Privacidade"
                  />
                </>
              ) : null}

              {tela === 'register' ? (
                <>
                  <CampoFalso tema={tema} rotulo="Seu nome" exemplo="Nome e sobrenome" />
                  <CampoFalso
                    tema={tema}
                    rotulo="Telefone com DDD"
                    exemplo="(11) 98765-4321"
                  />
                  <MarcacaoFalsa
                    tema={tema}
                    texto="Li e aceito os Termos de Uso e a Política de Privacidade"
                  />
                  <MarcacaoFalsa
                    tema={tema}
                    texto="Quero receber ofertas e novidades (opcional)"
                  />
                </>
              ) : null}

              {botao(tema.buttonLabel)}
            </>
          )}

          {rodape}
        </div>
      </div>
    </div>
  );
}

interface Props {
  mikrotikId: number;
  podeEscrever: boolean;
  /** Exibido acima do formulario. */
  aviso?: ReactNode;
  /** Botoes extras ao lado de Salvar (ex.: "Enviar para o MikroTik"). */
  acoesExtras?: ReactNode;
  /**
   * Modo de autenticacao vindo de fora. Quando o editor de modo esta na mesma
   * tela, quem manda e ele — inclusive antes de salvar, para a previa acompanhar
   * o clique. Ausente, este componente descobre sozinho pela API.
   */
  modo?: string;
}

export function ThemeEditor({ mikrotikId, podeEscrever, aviso, acoesExtras, modo }: Props) {
  const [tema, setTema] = useState<PortalThemeDto | null>(null);
  const [rascunho, setRascunho] = useState<Record<string, string>>({});
  const [logoId, setLogoId] = useState<number | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [enviandoLogo, setEnviandoLogo] = useState(false);
  /** Modo descoberto por conta propria, quando o pai nao informa. */
  const [modoLocal, setModoLocal] = useState<string>('open');
  const [tela, setTela] = useState<TelaPrevia>('open');

  const modoAtual = modo ?? modoLocal;

  useEffect(() => {
    // Com o modo vindo de fora, buscar aqui so criaria uma segunda verdade.
    if (modo !== undefined) return;

    /*
     * A previa precisa mostrar a tela que o cliente vai encontrar de verdade.
     * Editar cores e textos sobre o modelo do modo aberto, com o equipamento em
     * modo cadastro, esconde justamente onde os campos caem.
     */
    apiRequest<HotspotConfigDto>(`/admin/hotspot-config?mikrotikId=${mikrotikId}`)
      .then((config) => setModoLocal(config.effective.authMode))
      // Perfil sem permissao de leitura da configuracao: segue no modo aberto.
      .catch(() => undefined);
  }, [mikrotikId, modo]);

  // A tela exibida acompanha o modo; dentro de cadastro o operador escolhe qual.
  useEffect(() => {
    setTela(modoAtual === 'register' ? 'register' : modoAtual === 'hubsoft' ? 'cpf' : 'open');
  }, [modoAtual]);

  useEffect(() => {
    setTema(null);
    setErro(null);
    setOk(null);

    apiRequest<PortalThemeDto>(`/admin/portal-theme?mikrotikId=${mikrotikId}`)
      .then((dados) => {
        setTema(dados);
        setLogoId(dados.logoMediaId ?? null);
        setRascunho(
          Object.fromEntries(
            [...CORES, ...TEXTOS].map(({ chave }) => [
              chave,
              (dados[chave as keyof PortalThemeDto] as string | null) ?? '',
            ]),
          ),
        );
      })
      .catch((caught: unknown) =>
        setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao carregar'),
      );
  }, [mikrotikId]);

  /** O que o cliente veria: o rascunho por cima do que ja esta salvo. */
  function previa(): PortalThemeEffective {
    const combinado = { ...(tema?.effective ?? PADRAO) };
    for (const [chave, valor] of Object.entries(rascunho)) {
      if (valor) (combinado as Record<string, unknown>)[chave] = valor;
    }
    if (tema?.logoUrl) combinado.logoUrl = tema.logoUrl;
    return combinado;
  }

  async function salvar() {
    setSalvando(true);
    setErro(null);
    setOk(null);
    try {
      const corpo: Record<string, unknown> = { logoMediaId: logoId };
      for (const [chave, valor] of Object.entries(rascunho)) corpo[chave] = valor || null;

      setTema(
        await apiRequest<PortalThemeDto>(`/admin/portal-theme?mikrotikId=${mikrotikId}`, {
          method: 'PUT',
          body: corpo,
        }),
      );
      setOk('Aparência salva. O portal já usa a nova configuração.');
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao salvar');
    } finally {
      setSalvando(false);
    }
  }

  async function limpar() {
    if (!window.confirm('Voltar a aparência deste equipamento ao padrão de fábrica?')) return;
    try {
      const rota = `/admin/portal-theme?mikrotikId=${mikrotikId}`;
      await apiRequest(rota, { method: 'DELETE' });
      setTema(await apiRequest<PortalThemeDto>(rota));
      setLogoId(null);
      setRascunho(Object.fromEntries([...CORES, ...TEXTOS].map(({ chave }) => [chave, ''])));
      setOk('Personalização removida.');
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha ao limpar');
    }
  }

  async function enviarLogo(arquivo: File) {
    setEnviandoLogo(true);
    setErro(null);
    try {
      const midia = await enviarImagem(arquivo);
      setLogoId(midia.id);
      setTema((atual) => (atual ? { ...atual, logoUrl: midia.url } : atual));
      setOk('Logo enviado. Clique em Salvar para aplicar.');
    } catch (caught) {
      setErro(caught instanceof ApiRequestError ? caught.displayMessage : 'Falha no envio');
    } finally {
      setEnviandoLogo(false);
    }
  }

  if (tema === null) return <Spinner />;

  return (
    <div className="space-y-4">
      {erro ? <Alert>{erro}</Alert> : null}
      {ok ? <Alert tone="success">{ok}</Alert> : null}
      {aviso}

      <div className="grid gap-6 lg:grid-cols-[1fr_300px]">
        <div className="space-y-4">
          <Card title="Logo">
            <div className="flex flex-wrap items-center gap-4">
              {tema.logoUrl ? (
                <img
                  src={tema.logoUrl}
                  alt=""
                  className="max-h-16 max-w-[160px] rounded bg-slate-100 object-contain p-2"
                />
              ) : (
                <span className="text-sm text-slate-500">Nenhum logo definido.</span>
              )}

              {podeEscrever ? (
                <div className="flex gap-2">
                  <label className="inline-flex cursor-pointer items-center rounded-lg border border-slate-300 px-3 py-2 text-sm hover:bg-slate-50">
                    <input
                      type="file"
                      accept="image/jpeg,image/png,image/webp"
                      className="hidden"
                      disabled={enviandoLogo}
                      onChange={(event) => {
                        const arquivo = event.target.files?.[0];
                        if (arquivo) void enviarLogo(arquivo);
                        event.target.value = '';
                      }}
                    />
                    {enviandoLogo ? 'Enviando...' : 'Enviar logo'}
                  </label>

                  {logoId ? (
                    <Button
                      variant="ghost"
                      onClick={() => {
                        setLogoId(null);
                        setTema((atual) => (atual ? { ...atual, logoUrl: null } : atual));
                      }}
                    >
                      Remover
                    </Button>
                  ) : null}
                </div>
              ) : null}
            </div>
            <p className="mt-2 text-xs text-slate-500">
              PNG com fundo transparente fica melhor. Exibido com até 190×76 pixels.
            </p>
          </Card>

          <Card title="Cores">
            <div className="grid gap-3 sm:grid-cols-2">
              {CORES.map(({ chave, rotulo }) => (
                <label key={chave} className="block">
                  <span className="mb-1.5 block text-sm font-medium text-slate-700">{rotulo}</span>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      value={rascunho[chave] || (previa()[chave] as string)}
                      onChange={(event) =>
                        setRascunho((a) => ({ ...a, [chave]: event.target.value }))
                      }
                      disabled={!podeEscrever}
                      className="h-9 w-12 cursor-pointer rounded border border-slate-300"
                    />
                    <input
                      type="text"
                      value={rascunho[chave] ?? ''}
                      onChange={(event) =>
                        setRascunho((a) => ({ ...a, [chave]: event.target.value }))
                      }
                      placeholder={previa()[chave] as string}
                      disabled={!podeEscrever}
                      className="w-28 rounded-lg border border-slate-300 px-2 py-1.5 font-mono text-xs"
                    />
                    {rascunho[chave] ? (
                      <button
                        type="button"
                        onClick={() => setRascunho((a) => ({ ...a, [chave]: '' }))}
                        className="text-xs text-slate-500 underline"
                      >
                        padrão
                      </button>
                    ) : null}
                  </div>
                </label>
              ))}
            </div>
          </Card>

          <Card title="Textos">
            <div className="grid gap-3 sm:grid-cols-2">
              {TEXTOS.map(({ chave, rotulo, dica }) => (
                <Field
                  key={chave}
                  label={rotulo}
                  value={rascunho[chave] ?? ''}
                  onChange={(event) =>
                    setRascunho((a) => ({ ...a, [chave]: event.target.value }))
                  }
                  placeholder={previa()[chave] as string}
                  disabled={!podeEscrever}
                  hint={dica}
                />
              ))}
            </div>
          </Card>

          {podeEscrever ? (
            <div className="flex flex-wrap gap-2">
              <Button onClick={() => void salvar()} disabled={salvando}>
                {salvando ? 'Salvando...' : 'Salvar aparência'}
              </Button>
              {tema.id ? (
                <Button variant="ghost" onClick={() => void limpar()}>
                  Remover personalização
                </Button>
              ) : null}
              {acoesExtras}
            </div>
          ) : null}
        </div>

        <div className="lg:sticky lg:top-6 lg:self-start">
          <Card title="Prévia">
            {/* Duas telas no modo cadastro: quem chega e quem volta. */}
            {modoAtual === 'register' ? (
              <div className="mb-3 flex rounded-lg border border-slate-200 p-0.5 text-xs">
                {(
                  [
                    ['register', 'Primeiro acesso'],
                    ['retorno', 'Quem volta'],
                  ] as const
                ).map(([valor, rotulo]) => (
                  <button
                    key={valor}
                    type="button"
                    onClick={() => setTela(valor)}
                    className={`flex-1 rounded-md px-2 py-1.5 font-medium transition-colors ${
                      tela === valor
                        ? 'bg-brand-600 text-white'
                        : 'text-slate-600 hover:bg-slate-50'
                    }`}
                  >
                    {rotulo}
                  </button>
                ))}
              </div>
            ) : null}

            <PreviaPortal tema={previa()} tela={tela} />

            <p className="mt-3 text-center text-xs text-slate-500">
              {modoAtual === 'register'
                ? tela === 'retorno'
                  ? 'Cliente reconhecido dentro do prazo de cortesia: só confirma.'
                  : 'Assim o cliente vê ao conectar pela primeira vez.'
                : modoAtual === 'hubsoft'
                  ? 'O nome vem do HubSoft — o cliente digita só o CPF.'
                  : 'Assim o cliente vê ao conectar.'}
            </p>

            {modoAtual === 'register' || modoAtual === 'hubsoft' ? (
              <p className="mt-2 text-center text-[11px] text-slate-500">
                Os campos e os textos de aceite são fixos. O que você edita aqui — logo, cores,
                título, subtítulo, botão e rodapé — vale para todas as telas.
              </p>
            ) : null}
          </Card>
        </div>
      </div>
    </div>
  );
}
